import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Coffee, Loader2 } from 'lucide-react';
import Dialog from '../ui/Dialog';

/**
 * D-342 (updates D-336 §1, P-10e/D-85) — "הפסקה" while timers run: one
 * explanation line, "התחלת הפסקה" and "ביטול". NO timer list, no checkboxes,
 * no "מה עשיתי": the break PAUSES every running timer (the server does it when
 * `stop` is empty, D-98) and the work logs stay open; at the end of the break
 * the existing offer to resume them appears. Nothing is ever stopped here.
 * No ×, no Escape, no click-outside (D-336).
 */
export default function BreakTimersDialog({ onBreak, onCancel }: {
  /** Start the break; every timer is paused (nothing is stopped). */
  onBreak: () => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const primaryRef = useRef<HTMLButtonElement>(null);

  const run = async () => {
    setBusy(true);
    try {
      await onBreak();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      title={t('timer.breakWindow.title')}
      description={t('timer.breakWindow.intro')}
      icon={<Coffee size={20} strokeWidth={1.75} className="shrink-0 text-accent-text" aria-hidden />}
      size="md"
      dismissible={false}
      hideCloseButton
      initialFocus={primaryRef}
      panelProps={{ 'data-break-timers': '' }}
      footer={(
        <>
          <button
            type="button"
            className="btn btn-secondary flex-1 sm:flex-none"
            disabled={busy}
            onClick={onCancel}
            data-break-cancel=""
          >
            {t('timer.breakWindow.cancel')}
          </button>
          <button
            ref={primaryRef}
            type="button"
            className="btn btn-primary flex-1 sm:flex-none"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => void run()}
            data-break-stop=""
          >
            {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('timer.breakWindow.startBreak')}
          </button>
        </>
      )}
    >
      {null}
    </Dialog>
  );
}
